import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { Box, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { getAgentSession } from 'api/agent';
import { Main } from 'components/atoms/FlexContainers/FlexContainers';
import PageHeader from 'components/PageHeader';
import SiteHeader, { SITE_HEADER_HEIGHT } from 'components/SiteHeader/SiteHeader';
import DashboardFooter from 'components/StudiesDashboard/DashboardFooter';
import { find, first, isEmpty, size } from 'es-toolkit/compat';
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { useQueryWithErrorAndRetrySnackbar } from 'utils/useQueryWithErrorAndRetrySnackbar';
import { StudyReportScrollNavAttachmentInsets } from './StudyReportSectionsLayout';
import StudyReportStudySelector from './StudyReportStudySelector';
import {
  buildStudyReportCanvasCloseLocation,
  resolveStudyReportSelectedStudyIds,
  StudyReportRouteState,
} from './studyReportRouteNavigation';
import useStudyReportOptions from './useStudyReportOptions';

const REPORT_CANVAS_SCROLL_NAV_TOP_INSET = 8;

const REPORT_CANVAS_SCROLL_NAV_ATTACHMENT_INSETS: StudyReportScrollNavAttachmentInsets = {
  top: REPORT_CANVAS_SCROLL_NAV_TOP_INSET,
};

const StudyReportShell: React.FunctionComponent<React.PropsWithChildren<unknown>> = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { labId, queryLabId, setLabId } = useCurrentLabId();
  const {
    queryParams: { filters },
  } = useEncodedFilters();
  const sessionId = React.useMemo(() => new URLSearchParams(location.search).get('sessionId'), [location.search]);
  const sessionQuery = useQueryWithErrorAndRetrySnackbar(
    ['agentSession', sessionId],
    () => getAgentSession(sessionId as string),
    {
      enabled: Boolean(sessionId),
      staleTime: 10_000,
      queriedEntityName: 'copilot session',
    }
  );

  const sessionRecord = sessionQuery.data;
  const selectedStudyIds = React.useMemo(
    () => resolveStudyReportSelectedStudyIds({ filters, session: sessionRecord }),
    [filters, sessionRecord]
  );
  const routeState = location.state as StudyReportRouteState | null;
  const { studyOptions } = useStudyReportOptions();
  const hasAvailableStudies = Boolean(studyOptions?.length);

  React.useEffect(() => {
    const sessionLabId = sessionRecord?.sessionData?.reportState?.filters?.labId;
    if (!sessionLabId || queryLabId || sessionLabId === labId) {
      return;
    }

    setLabId(sessionLabId, 'replaceIn');
  }, [labId, queryLabId, sessionRecord, setLabId]);

  const studyScopeTitle = React.useMemo(() => {
    if (isEmpty(selectedStudyIds) || !studyOptions) return 'Analysis Report';
    if (size(selectedStudyIds) === 1) {
      const match = find(studyOptions, (opt) => opt.value === first(selectedStudyIds));
      return match?.text ? `${match.text} — Analysis Report` : 'Analysis Report';
    }
    return `Combined Analysis — ${size(selectedStudyIds)} Studies`;
  }, [selectedStudyIds, studyOptions]);
  const reportTitle = React.useMemo(
    () => sessionRecord?.title || sessionRecord?.sessionData?.reportState?.title || 'Analysis Report',
    [sessionRecord]
  );

  const handleCloseCanvas = React.useCallback(() => {
    navigate(
      buildStudyReportCanvasCloseLocation({
        selectedStudyIds,
        labId,
        keepDrawerOpen: new URLSearchParams(location.search).has('copilotDrawerOpen'),
      })
    );
  }, [labId, location.search, navigate, selectedStudyIds]);

  const handleCreateStudy = React.useCallback(() => {
    navigate(
      {
        pathname: '/',
        search: labId ? `?labId=${labId}` : '',
      },
      { state: { openCreateStudyModal: true } }
    );
  }, [labId, navigate]);

  const queryClient = useQueryClient();
  const isFetching = useIsFetching();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const handleRefreshCanvas = React.useCallback(() => {
    setIsRefreshing(true);
    void queryClient.refetchQueries({ type: 'active' });
  }, [queryClient]);

  React.useEffect(() => {
    if (isRefreshing && isFetching === 0) {
      setIsRefreshing(false);
    }
  }, [isFetching, isRefreshing]);

  return (
    <StaticPage>
      <Main leftDrawerOpen={false} rightDrawerOpen={false}>
        <SiteHeader />
        <Grid
          container
          direction="column"
          my={0}
          sx={{
            height: '100vh',
            width: '100%',
            minWidth: 0,
            flexWrap: 'nowrap',
            backgroundColor: theme.palette.mode === 'light' && theme.palette.grey[50],
          }}
        >
          <Box sx={{ height: SITE_HEADER_HEIGHT, flexShrink: 0 }} />

          <Grid
            container
            direction="column"
            px={6}
            pt={4}
            pb={0}
            spacing={3}
            sx={{ flex: 1, minHeight: 0, minWidth: 0, flexWrap: 'nowrap', overflowY: 'auto', overflowX: 'hidden' }}
          >
            <Grid item sx={{ mt: 0.5 }}>
              <PageHeader
                pageTitle={reportTitle}
                pageSubtitle={
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {studyScopeTitle}
                  </Typography>
                }
                trailingAction={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title="Refresh report data">
                      <IconButton onClick={handleRefreshCanvas} disabled={isRefreshing} size="small">
                        <RefreshRoundedIcon
                          sx={{
                            ...(isRefreshing && {
                              animation: 'spin 1s linear infinite',
                              '@keyframes spin': {
                                from: { transform: 'rotate(0deg)' },
                                to: { transform: 'rotate(360deg)' },
                              },
                            }),
                          }}
                        />
                      </IconButton>
                    </Tooltip>
                    <IconButton onClick={handleCloseCanvas} size="small">
                      <CloseRoundedIcon />
                    </IconButton>
                  </Stack>
                }
              />
            </Grid>
            <Grid item>
              <StudyReportStudySelector
                selectedStudyIds={selectedStudyIds}
                disabled={!hasAvailableStudies && !sessionQuery.isLoading}
              />
            </Grid>
            <Grid item sx={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
              <Box sx={{ display: 'flex', width: '100%', minWidth: 0, flex: 1 }}>
                <Outlet
                  context={{
                    selectedStudyId: selectedStudyIds[0],
                    selectedStudyIds,
                    hasAvailableStudies,
                    onCreateStudy: handleCreateStudy,
                    scrollNavAttachmentInsets: REPORT_CANVAS_SCROLL_NAV_ATTACHMENT_INSETS,
                    initialUserPrompt: routeState?.draftConversation?.initialUserPrompt,
                  }}
                />
              </Box>
            </Grid>
          </Grid>
          <DashboardFooter />
        </Grid>
      </Main>
    </StaticPage>
  );
};

export default StudyReportShell;

const StaticPage = styled(Box)(() => ({
  overflow: 'hidden',
}));
